import React, { useState } from 'react';
import { payrollAPI } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { money, downloadBlob } from './_util';

const CHECKS_SAMPLE = JSON.stringify([
  { employee_number: '1001', check_number: '555', gross: 3000, w4: { form_version: '2020', filing_status: 'single' }, state_setup: { work_state: 'OK', resident_state: 'OK', filing_status: 'single' } },
], null, 2);

const statusColor = (s) => (s === 'match' ? 'text-green-700' : 'text-red-700');

export const ComparisonSection = () => {
  const [payDate, setPayDate] = useState('2026-11-30');
  const [checks, setChecks] = useState(CHECKS_SAMPLE);
  const [runId, setRunId] = useState('');
  const [checkDate, setCheckDate] = useState('2026-11-30');
  const [parity, setParity] = useState(null);

  const runNet = async () => {
    let parsed;
    try { parsed = JSON.parse(checks); } catch { return toast.error('Checks must be a valid JSON array'); }
    try {
      const r = await payrollAPI.netRun({ pay_date: payDate, pay_frequency: 'semi-monthly', checks: parsed });
      setRunId(r.data.run_id);
      toast.success(`Computed ${r.data.computed} check(s). Run id filled in below.`);
      if (r.data.errors?.length) toast.error(`${r.data.errors.length} check(s) stopped on a missing table`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Net run failed'); }
  };

  const compare = async () => {
    try { const r = await payrollAPI.netParity(runId.trim(), checkDate); setParity(r.data); }
    catch (e) { toast.error(e?.response?.data?.detail || 'No parity found for that run / check date'); }
  };

  const exportFile = async (fmt) => {
    try { const r = await payrollAPI.netParityExport(runId.trim(), checkDate, fmt); downloadBlob(r, `net_parity_${checkDate}.${fmt === 'xlsx' ? 'xlsx' : 'csv'}`); }
    catch (e) { toast.error('Export failed — compare first'); }
  };

  return (
    <div className="space-y-6">
      <Card data-testid="netpay-run">
        <CardHeader><CardTitle>Parallel net-pay run</CardTitle>
          <CardDescription>Compute net pay for a set of synthetic checks using the Approved tables in effect on the pay date. Results are tagged parallel_test and never post to pay runs, ledger, banking or NACHA.</CardDescription></CardHeader>
        <CardContent className="space-y-3">
          <label className="text-sm">Pay date<Input data-testid="run-paydate" value={payDate} onChange={(e) => setPayDate(e.target.value)} className="w-40" /></label>
          <Textarea data-testid="run-checks" rows={5} value={checks} onChange={(e) => setChecks(e.target.value)} />
          <Button data-testid="run-net" onClick={runNet}>Run net pay</Button>
          {runId && <div className="text-xs text-slate-500" data-testid="run-id">Run id: {runId}</div>}
        </CardContent>
      </Card>

      <Card data-testid="netpay-parity">
        <CardHeader><CardTitle>Line-by-line comparison vs WinTeam</CardTitle>
          <CardDescription>Compare a net-run to the Final (or latest) WinTeam import for a check date — every line, to the penny — with an exportable detail.</CardDescription></CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-3 items-end">
            <label className="text-sm">Run id<Input data-testid="parity-runid" value={runId} onChange={(e) => setRunId(e.target.value)} className="w-80" /></label>
            <label className="text-sm">Check date<Input data-testid="parity-checkdate" value={checkDate} onChange={(e) => setCheckDate(e.target.value)} className="w-40" /></label>
            <Button data-testid="parity-load" onClick={compare}>Compare</Button>
            <Button variant="outline" data-testid="parity-export-csv" onClick={() => exportFile('csv')}>Export CSV</Button>
            <Button variant="outline" data-testid="parity-export-xlsx" onClick={() => exportFile('xlsx')}>Export Excel</Button>
          </div>

          {parity && (
            <div className="space-y-3" data-testid="parity-result">
              <div className="rounded border p-3 bg-slate-50" data-testid="parity-summary-card">
                <div className="text-base font-semibold">
                  {parity.summary.net_match_count}/{parity.summary.employees_total} employees match to the penny on net
                  <Badge className="ml-2" variant={parity.summary.net_match_pct === 100 ? 'default' : 'outline'}>{parity.summary.net_match_pct}%</Badge>
                </div>
                <div className="text-xs text-slate-500">WinTeam {parity.winteam_version} ({parity.winteam_status})</div>
                {parity.summary.top_difference_reasons?.length > 0 && (
                  <div className="mt-1 text-sm text-slate-600">Top reasons: {parity.summary.top_difference_reasons.map((r) => `${r[0]} (${r[1]})`).join(', ')}</div>
                )}
              </div>

              <div className="space-y-3">
                {(parity.employees || []).map((e) => (
                  <div key={e.employee_number} className="border rounded" data-testid={`parity-emp-${e.employee_number}`}>
                    <div className="px-3 py-2 bg-slate-100 text-sm font-medium flex justify-between">
                      <span>Employee {e.employee_number}</span>
                      <Badge variant={e.matched ? 'default' : 'destructive'}>{e.matched ? 'net matches' : (e.reason || 'difference')}</Badge>
                    </div>
                    {e.lines?.length > 0 && (
                      <table className="w-full text-sm">
                        <thead><tr className="text-left text-xs text-slate-500"><th className="p-2">Line</th><th>Ours</th><th>WinTeam</th><th>Status</th><th>Reason</th></tr></thead>
                        <tbody>
                          {e.lines.map((ln, i) => (
                            <tr key={i} className="border-t" data-testid={`parity-line-${e.employee_number}-${i}`}>
                              <td className="p-2">{ln.line}</td><td>{money(ln.ours_cents)}</td><td>{money(ln.winteam_cents)}</td>
                              <td className={statusColor(ln.status)}>{ln.status}</td><td className="text-xs text-slate-500">{ln.reason || ''}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
